// src/components/ProfileView.js
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { doc, getDoc, collection, query, orderBy, limit, getDocs } from "firebase/firestore";
import { db } from "../../firebase-config";
import RoundDetail from "../LowRound/RoundDetail";
import { getCompetitionWindowStartForDate } from "../../utils/competitionWindow";

// Helper function to compute totals.
const computeTotals = (scoreArray) => {
  let totalScore = 0;
  let totalPar = 0;
  if (Array.isArray(scoreArray)) {
    scoreArray.forEach((s) => {
      totalScore += s.score || 0;
      totalPar += s.par || 0;
    });
  }
  return { totalScore, totalPar };
};

// Subcomponent: ProfileHeader.
const ProfileHeader = ({ profile, onPhotoClick }) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
      {profile.photoURL ? (
        <img
          src={profile.photoURL}
          alt="Profile"
          style={{
            width: "100px",
            height: "100px",
            borderRadius: "50%",
            objectFit: "cover",
            marginRight: "16px",
            cursor: "pointer",
          }}
          onClick={onPhotoClick}
        />
      ) : (
        <Box
          sx={{
            width: "100px",
            height: "100px",
            borderRadius: "50%",
            backgroundColor: "#ccc",
            mr: 2,
          }}
        />
      )}
      <Box>
        <Typography variant="h6">
          {profile.firstName} {profile.lastName}
        </Typography>
        {profile.bagTag && (
          <Typography variant="subtitle1" color="text.secondary">
            Bag Tag: {profile.bagTag}
          </Typography>
        )}
        {profile.location && (
          <Typography variant="body2" color="text.secondary">
            {profile.location}
          </Typography>
        )}
        {profile.handicap && (
          <Typography variant="body2" color="text.secondary">
            Current Handicap: {profile.handicap}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// Subcomponent: RecentRounds.
const RecentRounds = ({ rounds, loading, onRoundSelect }) => {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Recent Rounds
      </Typography>
      {loading ? (
        <CircularProgress size={24} />
      ) : rounds.length > 0 ? (
        <List>
          {rounds.map((round) => {
            const { totalScore, totalPar } = computeTotals(round.scores);
            const scoreDiff = totalScore - totalPar;
            const differential =
              round.differential !== undefined
                ? round.differential >= 0
                  ? `+${round.differential.toFixed(1)}`
                  : round.differential.toFixed(1)
                : "N/A";
            return (
              <ListItem
                key={round.id}
                button
                onClick={() => onRoundSelect(round)}
                sx={{ cursor: "pointer" }}
              >
                <ListItemText
                  primary={`Course Played: ${round.clubName || "Unknown Club"}`}
                  secondary={`Total: ${totalScore} (${scoreDiff >= 0 ? `+${scoreDiff}` : scoreDiff}) | Differential: ${differential}`}
                />
              </ListItem>
            );
          })}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No recent rounds available.
        </Typography>
      )}
    </Paper>
  );
};

// Subcomponent: EnlargedPhotoDialog.
const EnlargedPhotoDialog = ({ open, onClose, photoURL }) => {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ m: 0, p: 2 }}>
        Profile Photo
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 2,
        }}
      >
        <Box
          component="img"
          src={photoURL}
          alt="Enlarged profile"
          sx={{
            display: "block",
            margin: "auto",
            maxWidth: "100%",
            maxHeight: "80vh",
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

// Main component: ProfileView.
const ProfileView = ({ open, onClose, userId }) => {
  const [profile, setProfile] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingRounds, setLoadingRounds] = useState(true);
  const [selectedRound, setSelectedRound] = useState(null);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const fetchProfile = async () => {
      try {
        const profileRef = doc(db, "users", userId);
        const profileSnap = await getDoc(profileRef);
        if (profileSnap.exists()) {
          setProfile({ ...profileSnap.data(), id: profileSnap.id });
        } else {
          console.error("No such user exists!");
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfile();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const fetchRounds = async () => {
      try {
        const roundsRef = collection(db, "users", userId, "scorecards");
        const roundsQuery = query(roundsRef, orderBy("roundDate", "desc"), limit(5));
        const roundsSnap = await getDocs(roundsQuery);
        const roundsList = roundsSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setRounds(roundsList);
      } catch (error) {
        console.error("Error fetching rounds:", error);
      } finally {
        setLoadingRounds(false);
      }
    };

    fetchRounds();
  }, [userId]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ m: 0, p: 2 }}>
        User Profile
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loadingProfile ? (
          <Box sx={{ textAlign: "center", my: 2 }}>
            <CircularProgress />
          </Box>
        ) : profile ? (
          <Box>
            <ProfileHeader profile={profile} onPhotoClick={() => setPhotoDialogOpen(true)} />
            <RecentRounds
              rounds={rounds}
              loading={loadingRounds}
              onRoundSelect={setSelectedRound}
            />
          </Box>
        ) : (
          <Typography variant="body1" color="error">
            Error: User profile not found.
          </Typography>
        )}
      </DialogContent>

      {/* Round detail dialog for individual round preview */}
      {selectedRound && profile && (
        <Dialog
          open={Boolean(selectedRound)}
          onClose={() => setSelectedRound(null)}
          fullWidth
          maxWidth="md"
        >
          <DialogContent dividers>
            <RoundDetail
              round={selectedRound}
              competitionWeek={getCompetitionWindowStartForDate(
                new Date(selectedRound.roundDate.seconds * 1000)
              )}
              placementIndex={0}
              playerName={`${profile.firstName} ${profile.lastName}`}
              playerBagTag={profile.bagTag}
              playerPhotoURL={profile.photoURL}
              isCompetition={selectedRound.isCompetition}
              showProfileInfo={false} // Only show course played header.
              onClose={() => setSelectedRound(null)}
            />
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={() => setSelectedRound(null)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Enlarged Profile Photo Dialog */}
      {profile && profile.photoURL && (
        <EnlargedPhotoDialog
          open={photoDialogOpen}
          onClose={() => setPhotoDialogOpen(false)}
          photoURL={profile.photoURL}
        />
      )}
    </Dialog>
  );
};

export default ProfileView;